import React from 'react'
import PropTypes from 'prop-types'
import {Tooltip} from 'antd'
import {formatVnDateTime, formatVnTime} from '../../helpers/format'

/**
 * Phụ đề của hai màn báo cáo M18: khoảng ngày · mốc chạy · mốc số liệu.
 *
 * ⚠️ Tách khỏi `OutputReportPage` để có chỗ KIỂM. Cùng lý do đã ghi cho `ReportNotices`: trong repo
 *    này không file test nào import một màn hình nào, nên mọi nhánh JSX nằm trong file 1.400 dòng
 *    đó có mutation score 0 theo cấu tạo. Và `eslint-plugin-react-hooks` bản repo đang dùng đếm
 *    nhánh trên TOÀN hàm — mỗi `{cond && ()}` thêm vào đó đẩy bộ đếm lên tới chỗ plugin báo NHẦM
 *    "useMemo is called conditionally" cho những hook không liên quan ở phía trên.
 *
 * ⚠️ Hiện NGÀY, không hiện giờ — và nói thẳng đó là ngày giờ VN. Báo cáo chỉ tính ngày VN nằm trọn
 *    (BR-2.1) và ô chọn ngày không còn chọn giờ, nên phần giờ ở đây không mang nghĩa nào: sau khi
 *    tải lại trang, `to` đọc từ URL là `moment('2026-10-28')` = 00:00 và phụ đề sẽ in
 *    "→ 28/10/2026 00:00" trong khi báo cáo tính tới hết ngày 28. Nhãn múi giờ MÁY cũng sai hướng
 *    — nó in GMT+00:00 trên máy không ở VN trong khi con số bên dưới luôn gom theo ngày VN.
 *
 * ⚠️ Hai MỐC (`tính lúc`, `số liệu đủ tới`) in theo giờ VIỆT NAM (`formatVnTime`/`formatVnDateTime`),
 *    không theo đồng hồ máy. Dòng này mở đầu bằng `(ngày giờ Việt Nam)` nên in tiếp hai con số theo
 *    múi giờ khác là tự mâu thuẫn ngay trong một câu: người xem từ UTC+0 đọc "số liệu đủ tới 10:05"
 *    rồi so với mọi con số khác trên màn hình — vốn gom theo ngày VN — và lệch bảy tiếng mà không
 *    có gì nói ra. (Vòng review 2, G-R7.)
 */
function ReportSubtitle({from, to, computedAt, freshness}) {
    return (
        <div className="VcPageSubtitle">
            {from.format('DD/MM/YYYY')}
            {' → '}
            {to.format('DD/MM/YYYY')}
            {' (ngày giờ Việt Nam)'}
            {computedAt && ` · tính lúc ${formatVnDateTime(computedAt)}`}

            {/*
              * ⚠️ `computedAt` trả lời "REQUEST chạy lúc nào", KHÔNG phải "số liệu đủ tới đâu".
              *    Trước khi có bảng tổng hợp thì hai thứ trùng nhau vì mọi lượt đọc đều quét log
              *    trực tiếp. Từ khi bảng là đường chính thì KHÔNG: báo cáo có thể `tính lúc 17:10`
              *    trong khi số của hôm nay mới đủ tới 17:02. Đọc "tính lúc 17:10" thành "số liệu
              *    tới 17:10" là hiểu sai rất tự nhiên, nên hai con số phải đứng CẠNH NHAU —
              *    chênh lệch giữa chúng tự giải thích chính nó.
              *
              * ⚠️ Ở đây chứ KHÔNG phải một Notice riêng: cổng `read()` của service chỉ phục vụ ô
              *    còn tươi trong hai nhịp chu kỳ, nên độ trễ LUÔN ≤ 2 × `OUTPUT_ROLLUP_CYCLE_MS`
              *    (mặc định 10 phút) — ca "trễ nhiều" không tồn tại được. Dựng một hộp cảnh báo
              *    cho một độ trễ có trần mười phút là dạy người dùng bỏ qua hộp cảnh báo.
              */}
            {freshness && (
                <Tooltip
                    title={
                        'Khoảng lọc có chứa hôm nay, mà hôm nay thì vẫn đang chạy. Sản lượng phát sinh sau '
                        + 'mốc này chưa có trong con số đang hiện; bảng tổng hợp tự làm mới vài phút một '
                        + 'lần. Tải lại trang để lấy số mới nhất.'
                    }
                >
                    <span className="VcHasTooltip">
                        {` · số liệu đủ tới ${formatVnTime(freshness.at)}`}
                    </span>
                </Tooltip>
            )}
        </div>
    )
}

ReportSubtitle.propTypes = {
    /** `moment` của khoảng ĐÃ ÁP DỤNG, không phải ô chọn đang gõ dở. */
    from: PropTypes.object.isRequired,
    to: PropTypes.object.isRequired,
    /** ISO từ service — lúc dựng RESPONSE, khác hẳn mốc số liệu. `null` khi chưa tải xong. */
    computedAt: PropTypes.string,
    /** Kết quả `freshnessOf(report.dataFreshAs)` — `null` nghĩa là số liệu đủ tới hết khoảng lọc. */
    freshness: PropTypes.shape({at: PropTypes.instanceOf(Date), minutes: PropTypes.number}),
}

ReportSubtitle.defaultProps = {
    computedAt: null,
    freshness: null,
}

export default ReportSubtitle
